import React from "react";
import {
  Rocket,
  TrendingUp,
  BadgeCheck,
  ShieldCheck,
  Headset,
} from "lucide-react";

const POINTS = [
  {
    Icon: Rocket,
    title: "Fast implementation",
    body: "Most engagements move from kickoff to live deployment in weeks, not quarters.",
  },
  {
    Icon: TrendingUp,
    title: "Measurable ROI",
    body: "Every project is scoped to a clear business KPI — time saved, leads booked, costs reduced.",
  },
  {
    Icon: BadgeCheck,
    title: "Swiss quality standards",
    body: "Precision, reliability and discretion — the way Swiss businesses expect to be served.",
  },
  {
    Icon: ShieldCheck,
    title: "Compliance-aware",
    body: "Experience designing AI for regulated industries, with auditable processes and controls.",
  },
  {
    Icon: Headset,
    title: "Long-term support",
    body: "We stay alongside your team to refine, retrain and extend the systems we build.",
  },
];

export const WhyUs = () => {
  return (
    <section
      id="why-us"
      data-testid="why-us-section"
      className="relative py-24 lg:py-32 bg-page"
    >
      <div className="mx-auto max-w-7xl px-5 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-end">
          <div className="lg:col-span-6">
            <span className="chip">
              <span className="chip-dot" />
              <span className="font-mono text-[12px] text-ink-200">
                Why City AI Studio
              </span>
            </span>
            <h2
              className="mt-7 headline"
              style={{ fontSize: "clamp(1.85rem, 4.2vw, 3.4rem)" }}
            >
              A partner — <em>not just a vendor.</em>
            </h2>
          </div>
          <div className="lg:col-span-6">
            <p className="text-[16.5px] lg:text-[17.5px] leading-[1.75] text-ink-200/80 font-light">
              We combine practical AI implementation with business process
              expertise to help companies adopt AI with confidence — from
              local SMEs to financial-services firms.
            </p>
          </div>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {POINTS.map((p, i) => (
            <div
              key={p.title}
              data-testid={`why-card-${i}`}
              className="card-surface p-7 lg:p-8"
            >
              <div className="flex items-start justify-between">
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#EFF4FF] to-[#DCE6FE] border border-brand-glow/30 flex items-center justify-center">
                  <p.Icon size={18} className="text-brand" strokeWidth={1.6} />
                </div>
                <span className="font-mono text-[11px] tracking-tight text-ink-200/50">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>
              <h3
                className="mt-7 text-[20px] tracking-tight text-ink-50"
                style={{ fontWeight: 500, letterSpacing: "-0.02em" }}
              >
                {p.title}
              </h3>
              <p className="mt-3 text-[15px] leading-[1.65] text-ink-200/80 font-light">
                {p.body}
              </p>
            </div>
          ))}

          {/* CTA card */}
          <a
            href="#contact"
            data-testid="why-cta-card"
            className="relative overflow-hidden rounded-[22px] bg-ink-50 text-white p-8 lg:p-10 group hover:-translate-y-0.5 transition-transform duration-500"
          >
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "radial-gradient(600px 300px at 100% 100%, rgba(96,165,250,0.22) 0%, rgba(96,165,250,0) 60%)",
              }}
            />
            <div className="relative">
              <span className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.18em] uppercase text-white/70">
                <span className="w-1.5 h-1.5 rounded-full bg-brand-glow" />
                Start the conversation
              </span>
              <h3
                className="mt-6 text-white tracking-tight"
                style={{
                  fontSize: "clamp(1.4rem, 2.2vw, 1.7rem)",
                  fontWeight: 400,
                  letterSpacing: "-0.025em",
                }}
              >
                Ready to see what AI could do for your business?
              </h3>
              <span className="mt-8 btn-pill-light inline-flex">
                Book Free Consultation
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path
                    d="M5 12h14M13 5l7 7-7 7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </div>
          </a>
        </div>
      </div>
    </section>
  );
};

export default WhyUs;
